import { Fade } from "@biggeo/bg-ui";
import { Box, Stack } from "@biggeo/bg-ui/lab";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import * as A from "fp-ts/Array";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import { GeoJSONSource } from "mapbox-gl";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { SetURLSearchParams } from "react-router-dom";

import { FilterObject, MapTheme } from "@biggeo/bg-server-lib/studio";
import debounce from "lodash/debounce";
import { MapLayoutTabs } from "../../../layouts/MapLayout";
import { FunctionContainer } from "../../FunctionContainer";
import { DEFAULT_LOGIC_OPERATOR } from "../../filter-criteria/utils/utils";
import { DataPointTooltip } from "../../map-tooltip/components/DataPointTooltip";
import { useMapData } from "../../redux/hooks";
import { mapDataActions } from "../../redux/model";
import { snapToView } from "../../utils/geometry-utils";
import { removeCustomLayers } from "../../utils/style-utils";
import { FunctionType, LastFunctionType } from "../../utils/type";
import { useMap } from "../context";
import { useInitializeMap } from "../hooks/hooks";
import { resizeMap } from "../utils/map-utils";
import Accreditation from "../views/Accreditation";
import MapControls from "../views/MapControls";
import MapInfoElements from "../views/MapInfoElements";
import MapLoadingView from "../views/MapLoadingView";
import { MapPromptPopup } from "../views/MapPromptPopup";
import ResetView from "../views/ResetView";
import SaveView from "../views/SaveView";
import { SelectedShapeFunctions } from "../views/SelectedShapeFunctions";
import {
    useMapStateUtilities,
    usePolygon,
    useSavedBoundaries,
} from "../context/hooks";
import { MapContextPolygonSource } from "../context/types";
import { setDatasetContext } from "../context/utils/general-utils";

export interface MapboxMapContainerProps {
    readonly channelId: string;
    readonly multiFilters: FilterObject[];
    readonly openSaveViewPopper?: boolean;
    readonly setOpenSaveViewPopper?: (value: boolean) => void;
    readonly handleSideMenu?: (v: boolean) => void;
    readonly savedViewId?: string;
    readonly setSearchParams?: SetURLSearchParams;
    readonly mapTemplateId?: string;
    readonly isLoading?: boolean;
    readonly mapStyle: MapTheme;
    readonly setNavTab?: (tab: MapLayoutTabs) => void;
    readonly hideSelectMode?: boolean;
    readonly openGenerateFilePage: boolean;
    readonly setOpenGenerateFilePage: React.Dispatch<
        React.SetStateAction<boolean>
    >;
}

export const MapboxMapContainer = ({
    channelId,
    openSaveViewPopper,
    setOpenSaveViewPopper,
    savedViewId,
    setSearchParams,
    mapTemplateId,
    mapStyle,
    setNavTab,
    hideSelectMode,
}: MapboxMapContainerProps) => {
    const {
        map,
        draw,
        isMapLoaded,
        datasets,
        socketResponses,
        polygons,
        functionType,
        viewport,
        isLoading,
    } = useMap();

    const { updateDataset } = setDatasetContext();

    const { setFunctionType } = useMapStateUtilities();

    const { deletePolygon, overridePolygons } = usePolygon();

    const dispatch = useDispatch();
    const savedView = useMapData();
    const isSavedViewPage = !!savedView;

    const bounds = map.current?.getBounds();

    const mainRef = useRef<HTMLDivElement>(null);

    const [lastFunctionType, setLastFunctionType] =
        useState<LastFunctionType>();

    const {
        changeProjection,
        projection,
        zoomIn,
        zoomOut,
        selectMode,
        isSelectMode,
        isDrawMode,
    } = useInitializeMap();

    const isDrawFunctionType =
        functionType === FunctionType.polygon ||
        functionType === FunctionType.radius ||
        functionType === FunctionType.square;

    const onClose = () => {
        draw.current?.changeMode("simple_select");

        if (isDrawFunctionType) {
            setLastFunctionType(functionType);
        }

        setFunctionType(FunctionType.viewport);
    };

    const hasShapes = !isEmpty(polygons);

    const hasSelectedBoundaries = !isEmpty(
        polygons.filter((p) => p.source === MapContextPolygonSource.boundary)
    );

    const hasDataSets = !isEmpty(
        pipe(
            datasets,
            A.filter((d) => d.isSelected)
        )
    );

    const hasPolygons = !isEmpty(polygons);

    const canResetView = hasShapes || hasDataSets || hasPolygons;

    const resetView = () => {
        if (draw.current && hasShapes) {
            draw.current.deleteAll();
            draw.current?.changeMode("simple_select");
            if (map.current) {
                removeCustomLayers(map.current);
            }
        }

        if (hasPolygons) {
            overridePolygons([]);
        }

        if (hasDataSets) {
            pipe(
                datasets,
                A.filter((d) => d.isSelected),
                A.map((d) => {
                    updateDataset({
                        dataSourceId: d.dataSource.id,
                        dataset: {
                            isVisible: false,
                            isSelected: false,
                            isTableViewed: false,
                            isLegendOpen: false,
                            filters: {
                                logicOperator: DEFAULT_LOGIC_OPERATOR,
                                filters: [],
                            },
                            selectedRows: [],
                            pinnedRows: [],
                        },
                    });
                })
            );
        }

        const source = map.current?.getSource(
            "saved-polygons"
        ) as GeoJSONSource;

        if (source) {
            source.setData({
                type: "FeatureCollection",
                features: [],
            });
        }

        setFunctionType(FunctionType.viewport);
        setLastFunctionType(undefined);
        selectMode(false);
        setSearchParams?.({});
        dispatch(mapDataActions.updateMapData(undefined));
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        // When they click on custom boundaries from map side menu
        if (functionType === FunctionType.savedPolygon) {
            draw.current?.changeMode("simple_select");
            selectMode(false);
        }
    }, [functionType]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(
            debounce(() => resizeMap({ map, isMapLoaded }), 60)
        );
        if (mainRef.current) {
            resizeObserver.observe(mainRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [isMapLoaded, map]);

    return (
        <Fade in={isMapLoaded} timeout={1500}>
            <Box
                ref={mainRef}
                sx={{
                    height: "100%",
                    width: "100%",
                    position: "relative",
                }}
            >
                <Box
                    id="map"
                    sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        lineHeight: 0,
                        borderRadius: (theme) => theme.radius.default,
                        overflow: "hidden",
                    }}
                />
                <MapPromptPopup />
                <MapInfoElements
                    responses={socketResponses.responses}
                    map={map}
                    datasets={datasets}
                    isMapLoaded={isMapLoaded}
                    parentRef={mainRef}
                />
                <FunctionContainer
                    onClose={onClose}
                    functionType={functionType}
                    setFunctionType={setFunctionType}
                    draw={draw}
                    resetView={resetView}
                    selectMode={selectMode}
                    isSelectMode={isSelectMode}
                    snapToView={() =>
                        snapToView({
                            features: polygons.map((p) => p.feature),
                            map,
                        })
                    }
                    isDrawMode={isDrawMode}
                    lastFunctionType={lastFunctionType}
                    setLastFunctionType={setLastFunctionType}
                    mapTemplateId={mapTemplateId}
                    deletePolygon={deletePolygon}
                    hideSelectMode={hideSelectMode}
                />
                <MapControls
                    changeProjection={changeProjection}
                    projection={projection}
                    zoomIn={zoomIn}
                    zoomOut={zoomOut}
                    channelId={channelId}
                />
                {openSaveViewPopper && (
                    <SaveView
                        openSaveViewPopper={openSaveViewPopper}
                        setOpenSaveViewPopper={setOpenSaveViewPopper}
                        bounds={bounds}
                        viewport={viewport}
                        draw={draw}
                        onCloseDraw={onClose}
                        savedViewId={savedViewId}
                        isMapLoaded={isMapLoaded}
                        mapStyle={mapStyle}
                        map={map}
                        datasets={datasets}
                    />
                )}
                <Accreditation />
                {canResetView && !openSaveViewPopper && !isSavedViewPage && (
                    <ResetView resetView={resetView} />
                )}
                {!!isLoading.data && hasDataSets && <MapLoadingView />}
                {!!isLoading.boundaries && (
                    <MapLoadingView content="Loading boundary..." />
                )}
                {!isSelectMode && !isDrawMode ? (
                    // Wrapping this in a Stack is necessary, otherwise it throws a Node error when you try
                    // to draw after opening the tooltip.
                    <Stack>
                        <DataPointTooltip />
                        {/* <HeatMapTooltip /> */}
                    </Stack>
                ) : null}
                {!hasSelectedBoundaries && (
                    <SelectedShapeFunctions
                        onClickEdit={
                            setNavTab
                                ? () => setNavTab("shapeLayers")
                                : undefined
                        }
                    />
                )}
            </Box>
        </Fade>
    );
};
